import { ErrorBoundary } from '../chrome/ErrorBoundary'
import { GateCard } from '../gates/GateCard'
import type { GateAnswer } from '../gates/GateShell'
import type { EngagementView, Entry } from '../../stream/reducer'
import { PHASES, agentFor } from '../../phases'

interface ConversationProps {
  view: EngagementView
  ask: string
  busy: boolean
  onAnswer: (gate: Record<string, unknown>, decision: GateAnswer) => void
  children?: React.ReactNode
}

/**
 * THE CENTRE COLUMN, from `Frontend/engagement.html`.
 *
 * <p><b>THERE IS NO COMPOSER ON THIS PAGE</b> — the mockup's first correction. Engagements are
 * created from the workspace, and `§3.3.2` deletes the `idle` state the old one needed. The
 * question is the opening turn and is pinned above the stream; it does not trail it.</p>
 *
 * <p><b>TURNS AND GATES ARE ONE SEQUENCE.</b> A card is rendered where it was raised — under the
 * turn that raised it and above the turn that followed — which is what makes the page a transcript
 * rather than a list of cards with prose somewhere else.</p>
 */
export function Conversation({ view, ask, busy, onAnswer, children }: ConversationProps)
  : React.JSX.Element {
  return (
    <main className="conv">
      {/* THE ASK, PINNED — ONE LINE, TRUNCATED, BY DESIGN. This is the copy that survives
          scrolling past the question; the whole of it is the opening turn below, and the `title`
          carries it here for a reader who hovers. */}
      <div className="askbar">
        <span className="mlabel">Asked</span>
        <q title={ask}>{ask}</q>
      </div>

      <div className="stream" id="stream">
        <div className="inner">
          <article className="turn you">
            <div className="who">
              <span className="mono-badge" style={{ background: 'var(--grey4)', color: '#fff' }}
                    aria-hidden="true">YOU</span>
              <span className="role">You</span>
            </div>
            <div className="body">
              <p className="q">{ask}</p>
            </div>
          </article>

          {view.timeline.map((entry, i) => (
            <TimelineEntry key={i} entry={entry} view={view} busy={busy} onAnswer={onAnswer} />
          ))}

          {children}
        </div>
      </div>
    </main>
  )
}

function TimelineEntry({ entry, view, busy, onAnswer }:
                       { entry: Entry; view: EngagementView; busy: boolean
                         onAnswer: (gate: Record<string, unknown>, decision: GateAnswer) => void })
  : React.JSX.Element | null {
  switch (entry.kind) {
    case 'phase':
      // A PHASE IS NOT A TURN and gets no bubble of its own — the rail carries position. It ends
      // the previous block of prose, which the reducer has already done.
      return null

    case 'prose': {
      const phase = PHASES.find((p) => p.number === entry.phase)
      return (
        <article className="turn">
          <div className="who">
            <span className={`mono-badge ${agentFor(entry.phase).badge}`} aria-hidden="true">
              {agentFor(entry.phase).monogram}
            </span>
            <span className="role">
              {agentFor(entry.phase).name}
              {phase !== undefined && <span className="sub">phase {phase.number} · {phase.name}</span>}
            </span>
          </div>
          <div className="body">
            {entry.text.split(/\n{2,}/).filter((p) => p.trim().length > 0).map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        </article>
      )
    }

    case 'gate': {
      const id = String(entry.frame['gate_id'] ?? entry.frame['gateId'] ?? '')
      const open = entry.answer === null
      return (
        <div id={open && view.gate !== null
                 && String(view.gate['gate_id'] ?? view.gate['gateId'] ?? '') === id
                 ? 'gate' : undefined}>
          <ErrorBoundary fallback={
            <div className="inote" role="alert">
              <p className="mlabel">Card unavailable</p>
              <p style={{ margin: 0 }}>
                This card could not be displayed. The rest of the engagement is unaffected; if you
                report this, quote <code>{id}</code>.
              </p>
            </div>
          }>
            <GateCard frame={entry.frame} busy={busy} answer={entry.answer}
                      onAnswer={(decision) => onAnswer(entry.frame as Record<string, unknown>, decision)} />
          </ErrorBoundary>
        </div>
      )
    }

    case 'note':
      // THE ELIGIBILITY FUNNEL, in the conversation where the method choice it explains sits.
      return (
        <div className="inote">
          <p className="mlabel">Eligibility</p>
          <p style={{ margin: 0 }}>
            {String(entry.frame['declared'] ?? '')} methods registered →{' '}
            <b>{String(entry.frame['eligible'] ?? '')} eligible</b>.
          </p>
          {Array.isArray(entry.frame['stages']) && (
            <details>
              <summary>Show the funnel</summary>
              <p className="funnel">
                {(entry.frame['stages'] as unknown[]).map((stage, i) => (
                  <span key={i}>
                    {i > 0 && <br />}
                    {typeof stage === 'string' ? stage : JSON.stringify(stage)}
                  </span>
                ))}
              </p>
            </details>
          )}
        </div>
      )
  }
}

